/*
 * Auto payout, on the Wallet page. A partner who turns it on is paid their
 * commission once a month without pressing Withdraw: on the payout day the
 * backend takes everything available in the wallet and pays it to the bank
 * account through RazorpayX (GET/PUT /auto-payout). GST and TDS come off as for
 * a withdrawal, and the payout skips the finance approval step. It appears in
 * the withdrawals list marked "Auto payout".
 *
 * The payout day and the minimum are Wealthoak's to set; the partner only
 * chooses on or off. A switch needs a bank account, so without one the card
 * points at the bank card instead of failing on the server's 409.
 *
 * A backend that doesn't have the route yet answers 404. That is not a failure
 * the partner can retry, so the card says the feature isn't available yet
 * instead of showing "Not Found" with a Try again button.
 */

import { useState } from "react";
import { ApiError, errorMessage, wallet as walletApi, type AutoPayout, type Wallet } from "../api/client";
import { useLoad } from "../api/useLoad";
import { useMe } from "../auth/AuthContext";
import { LoadError, SkeletonRows, Status, WITHDRAWAL_STATUS, type Notify } from "./bits";
import { fmtDate, inr, ordinal } from "./format";
import { Info } from "./icons";

export default function AutoPayoutCard({ wallet, notify }: { wallet: Wallet | null; notify: Notify }) {
  const me = useMe();
  const ap = useLoad<AutoPayout | "unavailable">(() =>
    walletApi.autoPayout().catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 404) return "unavailable" as const;
      throw err;
    }),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unavailable = ap.data === "unavailable";
  const data = ap.data === "unavailable" ? null : ap.data;
  const blocked: string | null =
    me.status !== "active"
      ? "Auto payout opens once Wealthoak approves your partner account."
      : wallet && !wallet.payout_account && !data?.enabled
        ? "Add your bank account below to turn on auto payout."
        : null;

  async function toggle() {
    if (!data) return;
    setBusy(true);
    setError(null);
    try {
      const next = await walletApi.setAutoPayout(!data.enabled);
      ap.setData(next);
      notify(
        next.enabled
          ? `Auto payout is on. Your available commission is paid to your bank on the ${ordinal(next.payout_day)} of each month.`
          : "Auto payout is off. You can still withdraw whenever you like.",
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card card--pad auto-pay" aria-labelledby="auto-pay-title" aria-busy={ap.loading}>
      <div className="card-heading">
        <div>
          <p className="eyebrow">Monthly payout</p>
          <h2 id="auto-pay-title">Auto payout</h2>
        </div>
        {data && (
          <button
            type="button"
            role="switch"
            aria-checked={data.enabled}
            aria-label="Auto payout"
            aria-describedby={blocked ? "auto-pay-blocked" : undefined}
            className="switch"
            disabled={busy || Boolean(blocked)}
            onClick={() => void toggle()}
          >
            <i aria-hidden="true" />
          </button>
        )}
      </div>

      {ap.error && !data ? (
        <LoadError message={ap.error} onRetry={ap.reload} />
      ) : unavailable ? (
        <p className="wallet-hero__why">
          <Info size={14} />
          Auto payout isn't available yet. You can still withdraw your available commission whenever you like.
        </p>
      ) : !data ? (
        <SkeletonRows rows={2} label="Loading auto payout" />
      ) : (
        <>
          <p className="auto-pay__lead">
            Get paid without asking. On the {ordinal(data.payout_day)} of every month, everything available in your
            wallet is sent to your bank account through RazorpayX. GST and TDS come off exactly as they do for a
            withdrawal. An available balance under {inr(data.min_amount)} waits for the next month.
          </p>

          <dl className="kv auto-pay__kv">
            <div>
              <dt>Status</dt>
              <dd>
                <Status tone={data.enabled ? "green" : "muted"}>{data.enabled ? "On" : "Off"}</Status>
              </dd>
            </div>
            {data.enabled && data.next_run && (
              <div>
                <dt>Next payout</dt>
                <dd>{fmtDate(data.next_run)}</dd>
              </div>
            )}
            {data.last_run && (
              <div>
                <dt>Last payout</dt>
                <dd>
                  {inr(data.last_run.amount)} on {fmtDate(data.last_run.date)}{" "}
                  <Status tone={WITHDRAWAL_STATUS[data.last_run.status].tone}>
                    {WITHDRAWAL_STATUS[data.last_run.status].label}
                  </Status>
                </dd>
              </div>
            )}
          </dl>

          {blocked && (
            <p id="auto-pay-blocked" className="wallet-hero__why">
              <Info size={14} />
              {blocked}
            </p>
          )}
          {error && (
            <p className="field__error" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </section>
  );
}
